'use client';
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';

// Backend integration: GET /api/reports/ontime?range=7d
const dailyOnTime = [
  { date: 'Mar 22', rate: 91, late: 9, target: 90 },
  { date: 'Mar 23', rate: 88, late: 12, target: 90 },
  { date: 'Mar 24', rate: 94, late: 6, target: 90 },
  { date: 'Mar 25', rate: 87, late: 13, target: 90 },
  { date: 'Mar 26', rate: 82, late: 18, target: 90 },
  { date: 'Mar 27', rate: 89, late: 11, target: 90 },
  { date: 'Mar 28', rate: 84, late: 16, target: 90 },
];

const lateReasons = [
  { reason: 'Traffic / Road Conditions', count: 14, pct: 38 },
  { reason: 'Job Site Not Ready', count: 9, pct: 24 },
  { reason: 'Equipment Loading Delay', count: 7, pct: 19 },
  { reason: 'Crew Availability', count: 5, pct: 14 },
  { reason: 'GPS / Navigation Issue', count: 2, pct: 5 },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-zinc-400 mb-1">{label}</p>
      <p className="text-sm font-bold text-amber-400">{payload[0]?.value}% on-time</p>
    </div>
  );
};

export default function OnTimeReport({ dateRange }: { dateRange: string }) {
  const avg = Math.round(dailyOnTime.reduce((s, d) => s + d.rate, 0) / dailyOnTime.length);
  const missedTarget = dailyOnTime.filter((d) => d.rate < 90).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Avg On-Time Rate</p>
          <p className={`text-3xl font-bold tabular-nums ${avg >= 90 ? 'text-emerald-400' : 'text-amber-400'}`}>{avg}%</p>
          <p className="text-xs text-zinc-500 mt-1">Target: 90%</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Days Below Target</p>
          <p className="text-3xl font-bold tabular-nums text-red-400">{missedTarget}</p>
          <p className="text-xs text-zinc-500 mt-1">of {dailyOnTime.length} days</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Total Late Arrivals</p>
          <p className="text-3xl font-bold tabular-nums text-amber-400">37</p>
          <p className="text-xs text-zinc-500 mt-1">Avg +14 min late</p>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-zinc-200 mb-4">Daily On-Time Rate vs 90% Target — {dateRange}</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={dailyOnTime} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(39 39 42)" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'rgb(113 113 122)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'rgb(113 113 122)' }} axisLine={false} tickLine={false} domain={[70, 100]} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <ReferenceLine y={90} stroke="#F59E0B" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: 'Target 90%', fill: '#F59E0B', fontSize: 10, position: 'right' }} />
            <Bar dataKey="rate" radius={[4, 4, 0, 0]}>
              {dailyOnTime.map((entry) => (
                <Cell key={`cell-ontime-${entry.date}`} fill={entry.rate >= 90 ? '#34D399' : '#F87171'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-zinc-200 mb-4">Late Arrival Root Causes</h3>
        <div className="space-y-3">
          {lateReasons.map((r) => (
            <div key={`reason-${r.reason}`} className="flex items-center gap-3">
              <span className="text-xs text-zinc-400 w-48 flex-shrink-0">{r.reason}</span>
              <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-amber-500/70" style={{ width: `${r.pct}%` }} />
              </div>
              <span className="text-xs font-mono text-zinc-400 w-16 text-right">{r.count} jobs ({r.pct}%)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}